import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Card, CardBody } from '@nextui-org/react';
import type { WholesaleRecord, RetailRecord, DataSource, UnitMode } from './types';
import {
  shortMarket,
  wholesalePriceInUnit,
  retailPriceInUnit,
  fiveNumberSummary,
  MARKET_COLORS,
  REGION_COLORS,
} from './utils';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { EmptyState, KpiCard } from '../../components/ui';

interface DailyTabProps {
  wholesaleData: WholesaleRecord[];
  retailData: RetailRecord[];
  dataSource: DataSource;
  unit: UnitMode;
  commodity: string;
}

const DailyTab = ({ wholesaleData, retailData, dataSource, unit, commodity }: DailyTabProps) => {
  const { darkMode } = useUserStore();
  const { t } = useTranslation('usda-market');
  const unitLabel = unit === 'kg' ? '$/kg' : '$/lb';

  // Wholesale/FOB: use ALL data in range
  const wholesaleDispersion = useMemo(() => {
    if (dataSource === 'retail') return null;
    if (wholesaleData.length === 0) return null;

    const marketPrices: Record<string, number[]> = {};
    const varietyPrices: Record<string, number[]> = {};
    const allPrices: number[] = [];

    wholesaleData.forEach((r) => {
      const p = wholesalePriceInUnit(r, unit);
      if (p == null) return;
      allPrices.push(p);

      const m = shortMarket(r.market_location_name || '');
      if (m) {
        if (!marketPrices[m]) marketPrices[m] = [];
        marketPrices[m].push(p);
      }

      if (r.variety && r.variety !== 'N/A') {
        if (!varietyPrices[r.variety]) varietyPrices[r.variety] = [];
        varietyPrices[r.variety].push(p);
      }
    });

    return { marketPrices, varietyPrices, allPrices };
  }, [wholesaleData, dataSource, unit]);

  // Retail: use ALL data in range
  const retailDispersion = useMemo(() => {
    if (dataSource !== 'retail') return null;
    if (retailData.length === 0) return null;

    const regionPrices: Record<string, number[]> = {};
    const varietyPrices: Record<string, number[]> = {};
    const allPrices: number[] = [];

    retailData.forEach((r) => {
      const p = retailPriceInUnit(r, unit, commodity);
      if (p == null) return;
      allPrices.push(p);

      if (r.region) {
        if (!regionPrices[r.region]) regionPrices[r.region] = [];
        regionPrices[r.region].push(p);
      }

      if (r.variety && r.variety !== 'N/A') {
        if (!varietyPrices[r.variety]) varietyPrices[r.variety] = [];
        varietyPrices[r.variety].push(p);
      }
    });

    return { regionPrices, varietyPrices, allPrices };
  }, [retailData, dataSource, unit, commodity]);

  const buildBoxPlotChart = (
    pricesMap: Record<string, number[]>,
    title: string,
    colorMap: Record<string, string>
  ): { options: ApexOptions; series: ApexAxisChartSeries } | null => {
    const categories = Object.keys(pricesMap)
      .filter((k) => pricesMap[k].length >= 2)
      .sort((a, b) => {
        const avgA = pricesMap[a].reduce((s, v) => s + v, 0) / pricesMap[a].length;
        const avgB = pricesMap[b].reduce((s, v) => s + v, 0) / pricesMap[b].length;
        return avgB - avgA;
      });
    if (categories.length === 0) return null;

    const boxData = categories.map((cat) => {
      const summary = fiveNumberSummary(pricesMap[cat]);
      return { x: cat, y: summary || [0, 0, 0, 0, 0] };
    });

    return {
      series: [{ type: 'boxPlot', data: boxData }],
      options: {
        chart: {
          type: 'boxPlot',
          height: Math.max(350, categories.length * 50),
          foreColor: darkMode ? '#A1A1AA' : '#71717A',
        },
        plotOptions: {
          boxPlot: {
            colors: { upper: '#e11d48', lower: '#3b82f6' },
          },
        },
        xaxis: { labels: { style: { fontSize: '11px' } } },
        yaxis: {
          title: { text: unitLabel },
          labels: { formatter: (v: number) => `$${v?.toFixed(2) ?? ''}` },
        },
        tooltip: {
          shared: false,
          custom: ({ seriesIndex, dataPointIndex, w }: { seriesIndex: number; dataPointIndex: number; w: { config: { series: { data: { x: string; y: number[] }[] }[] } } }) => {
            const point = w.config.series[seriesIndex].data[dataPointIndex];
            const [min, q1, med, q3, max] = point.y;
            const count = pricesMap[point.x]?.length || 0;
            return `<div class="p-2 text-xs">
              <b>${point.x}</b> (${count} records)<br/>
              Max: $${max.toFixed(2)}<br/>
              Q3: $${q3.toFixed(2)}<br/>
              Median: $${med.toFixed(2)}<br/>
              Q1: $${q1.toFixed(2)}<br/>
              Min: $${min.toFixed(2)}
            </div>`;
          },
        },
        title: { text: title, style: { fontSize: '15px', fontWeight: 600 } },
        colors: categories.map((c) => colorMap[c] || '#6b7280'),
      },
    };
  };

  // Wholesale / FOB view
  if (dataSource !== 'retail' && wholesaleDispersion) {
    const { marketPrices, varietyPrices, allPrices } = wholesaleDispersion;
    const sourceLabel = dataSource === 'wholesale' ? 'Wholesale (Terminal)' : 'FOB (Shipping Point)';
    const avg = allPrices.length ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length : 0;
    const min = allPrices.length ? Math.min(...allPrices) : 0;
    const max = allPrices.length ? Math.max(...allPrices) : 0;

    const marketChart = buildBoxPlotChart(
      marketPrices,
      `${commodity} ${sourceLabel} — ${t('Dispersión por Mercado')}`,
      MARKET_COLORS
    );
    const varietyChart = buildBoxPlotChart(
      varietyPrices,
      `${commodity} ${sourceLabel} — ${t('Dispersión por Variedad')}`,
      {}
    );

    return (
      <div className='space-y-4 pt-4'>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
          <KpiCard label={t('Records')} value={String(allPrices.length)} />
          <KpiCard label={`${t('Avg')} ${unitLabel}`} value={`$${avg.toFixed(2)}`} />
          <KpiCard label={`${t('Min')} ${unitLabel}`} value={`$${min.toFixed(2)}`} />
          <KpiCard label={`${t('Max')} ${unitLabel}`} value={`$${max.toFixed(2)}`} />
        </div>

        {marketChart && (
          <Card>
            <CardBody>
              <ReactApexChart
                options={marketChart.options}
                series={marketChart.series}
                type='boxPlot'
                height={Math.max(350, Object.keys(marketPrices).length * 50)}
              />
            </CardBody>
          </Card>
        )}

        {varietyChart && (
          <Card>
            <CardBody>
              <ReactApexChart
                options={varietyChart.options}
                series={varietyChart.series}
                type='boxPlot'
                height={Math.max(350, Object.keys(varietyPrices).length * 50)}
              />
            </CardBody>
          </Card>
        )}
      </div>
    );
  }

  // Retail view
  if (dataSource === 'retail' && retailDispersion) {
    const { regionPrices, varietyPrices, allPrices } = retailDispersion;
    const avg = allPrices.length ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length : 0;
    const min = allPrices.length ? Math.min(...allPrices) : 0;
    const max = allPrices.length ? Math.max(...allPrices) : 0;

    const regionChart = buildBoxPlotChart(
      regionPrices,
      `${commodity} Retail — ${t('Dispersión por Región')}`,
      REGION_COLORS
    );
    const varietyChart = buildBoxPlotChart(
      varietyPrices,
      `${commodity} Retail — ${t('Dispersión por Variedad')}`,
      {}
    );

    return (
      <div className='space-y-4 pt-4'>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
          <KpiCard label={t('Records')} value={String(allPrices.length)} />
          <KpiCard label={`${t('Avg')} ${unitLabel}`} value={`$${avg.toFixed(2)}`} />
          <KpiCard label={`${t('Min')} ${unitLabel}`} value={`$${min.toFixed(2)}`} />
          <KpiCard label={`${t('Max')} ${unitLabel}`} value={`$${max.toFixed(2)}`} />
        </div>

        {regionChart && (
          <Card>
            <CardBody>
              <ReactApexChart
                options={regionChart.options}
                series={regionChart.series}
                type='boxPlot'
                height={Math.max(350, Object.keys(regionPrices).length * 50)}
              />
            </CardBody>
          </Card>
        )}

        {varietyChart && (
          <Card>
            <CardBody>
              <ReactApexChart
                options={varietyChart.options}
                series={varietyChart.series}
                type='boxPlot'
                height={Math.max(350, Object.keys(varietyPrices).length * 50)}
              />
            </CardBody>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className='mt-4'>
      <EmptyState title={t('No hay datos disponibles')} />
    </div>
  );
};

export default DailyTab;
